import { FileMetadata } from "@features/cx/shared/types/consumerRequest";
import { Stepper } from "@shared/components/Stepper";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import React, { useEffect, useState } from "react";
import { useLocation, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { PageHeader } from "@shared/ui/pageHeader";
import { ComplaintDetailsStep } from "./stepComponents/ComplaintDetailsStep";
import { CustomerInformationStep } from "./stepComponents/CustomerInformationStep";
import { EvidenceAttachmentsStep } from "./stepComponents/EvidenceAttachmentsStep";
import { ReviewSubmitStep } from "./stepComponents/ReviewSubmitStep";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";

// Complaint-specific data structures
interface CustomerInfo {
  accountNumber: string;
  name: string;
  address: string;
  phone: string;
  email: string;
  customerType: string;
  customerSince?: string;
}

interface ComplaintDetails {
  category: string;
  complaintName: string;
  subCategory: string;
  priority: string;
  description: string;
  expectedResolution: string;
  incident: {
    date: string;
  };
}

interface EvidenceData {
  notes: string;
  attachments?: File[];
}

interface ComplaintData {
  customer: CustomerInfo;
  complaint: ComplaintDetails;
  evidence: EvidenceData;
}

// Initial data structures
const initialCustomer: CustomerInfo = {
  accountNumber: "",
  name: "",
  address: "",
  phone: "",
  email: "",
  customerType: "",
  customerSince: "",
};

const initialComplaint: ComplaintDetails = {
  category: "",
  complaintName: "",
  subCategory: "",
  priority: "",
  description: "",
  expectedResolution: "",
  incident: {
    date: "",
  },
};

const initialEvidence: EvidenceData = {
  notes: "",
  attachments: [],
};

const initialComplaintData: ComplaintData = {
  customer: initialCustomer,
  complaint: initialComplaint,
  evidence: initialEvidence,
};

export const CreateComplaintRequest: React.FC = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const params = useParams();
  const storageKey = `complaint-request-progress-${remoteUtilityId}`;

  // Extract consumerId and consumerNo from URL parameters
  const urlConsumerId = params.consumerId ? parseInt(params.consumerId) : null;
  const urlConsumerNo = params.consumerNo || null;
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const source = location.state?.source || searchParams.get('source') || null;

  const { startTracking, logTimeUntilStep, stopTracking } = useFormCompletionTracker("Complaint");
  const navigate = useNavigate();

  const handleBack = (replace = false) => {
    if (source === "account-management") {
      const backConsumerNo = urlConsumerNo || searchParams.get("search") || location.state?.prefilledConsumer?.consumerNo;
      if (backConsumerNo) {
        navigate(`/cx/consumer-management/${backConsumerNo}`, { replace });
        return;
      }
    }
    navigate("/cx/complaints", { replace });
  };

  useEffect(() => {
    const handlePopState = () => {
      if (!window.location.hash) {
        // when hash is cleared, redirect to parent route
        handleBack(true);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate, source, urlConsumerNo, searchParams, location.state]);

  useEffect(() => { startTracking() }, [startTracking])

  // File state management at parent level
  const [fileObjects, setFileObjects] = useState<File[]>([]);
  const [fileMetadata, setFileMetadata] = useState<FileMetadata[]>([]);
  const [evidenceFormData, setEvidenceFormData] = useState<any>({});
  console.log("fileObjects", fileObjects);

  // Handle file data changes from EvidenceAttachmentsStep
  const handleFileDataChange = (data: any) => {
    // 1. Update metadata and form data
    setFileMetadata(data.fileMetadata || []);
    setEvidenceFormData({
      notes: data.notes || "",
      incidentDate: data.incidentDate || "",
      expectedResolution: data.expectedResolution || "",
    });

    // 2. Reconcile File Objects (The actual binary data)
    if (data.file) {
      setFileObjects(prevFiles => {
        // If the child sent a full list (active session), we can use it
        // Or if it's an addition, we merge.
        // We keep previous files that are still present in the metadata 
        // (if we could match them, but simpler is to just trust the child's 'file' array if provided)

        // If the child sent an empty array but we have metadata, it means it's in "restored" state
        // and doesn't want to wipe our files.
        const hasRestoredFiles = (data.fileMetadata || []).some((f: any) => f.needsReupload);

        if (data.file.length === 0 && hasRestoredFiles) {
          // Keep existing files that match the new metadata
          const currentMetadataIds = new Set((data.fileMetadata || []).map((m: any) => m.id));
          // Note: This assumes metadata ID is stable, which it is in our implementation
          return prevFiles;
        }

        return data.file;
      });
    }
  };

  // Store the handler on window for child components to access
  React.useEffect(() => {
    (window as any).handleFileDataChange = (data: any) => {
      if (data.file?.length > 0) {
        setFileObjects(data.file);
      }
      setFileMetadata(data.fileMetadata || []);
      setEvidenceFormData({
        notes: data.notes || "",
        incidentDate: data.incidentDate || "",
        expectedResolution: data.expectedResolution || "",
      });
    };

    // Store file data on window for ReviewSubmitStep to access
    (window as any).fileData = {
      fileObjects,
      fileMetadata,
      evidenceFormData,
    };

    return () => {
      delete (window as any).handleFileDataChange;
      delete (window as any).fileData;
    };
  }, [fileObjects, fileMetadata, evidenceFormData]);

  // Stepper steps array
  const steps = [
    {
      title: "Customer Information",
      slug: "customer-info",
      component: (
        <CustomerInformationStep
          remoteUtilityId={Number(remoteUtilityId)}
          storageKey={storageKey}
          logTimeUntilStep={logTimeUntilStep}
          urlConsumerId={urlConsumerId}
          urlConsumerNo={urlConsumerNo}
          source={source}
        />
      ),
      optional: false,
      infoTitle: "Customer Information",
      infoDescription: "Select customer and verify details",
    },
    {
      title: "Complaint Details",
      slug: "complaint-details",
      component: (
        <ComplaintDetailsStep
          remoteUtilityId={Number(remoteUtilityId)}
          storageKey={storageKey}
          logTimeUntilStep={logTimeUntilStep}
        />
      ),
      optional: false,
      infoTitle: "Complaint Details",
      infoDescription: "Specify complaint category and details",
    },
    {
      title: "Evidence & Attachments",
      slug: "evidence-attachments",
      component: (
        <EvidenceAttachmentsStep
          remoteUtilityId={Number(remoteUtilityId)}
          storageKey={storageKey}
          onDataChange={handleFileDataChange}
          logTimeUntilStep={logTimeUntilStep}
        />
      ),
      optional: false,
      infoTitle: "Evidence & Attachments",
      infoDescription: "Upload supporting documents",
    },
    {
      title: "Review & Submit",
      slug: "review-submit",
      component: (
        <ReviewSubmitStep
          remoteUtilityId={Number(remoteUtilityId)}
          storageKey={storageKey}
          fileObjects={fileObjects} // ADD THIS LINE
          fileMetadata={fileMetadata} // ADD THIS LINE
          evidenceFormData={evidenceFormData} // ADD THIS LINE
          logTimeUntilStep={logTimeUntilStep}
          stopTracking={stopTracking}
        />
      ),
      optional: false,
      infoTitle: "Review & Submit",
      infoDescription: "Review and submit the complaint",
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Create Complaint Request"
        description="Complete the following steps to submit a complaint request."
        onBack={() => handleBack()}
      />
      <Stepper
        steps={steps}
        onStepChange={() => { }}
        onValidation={async () => ({ isValid: true })}
        onComplete={() => { }}
        onStepSave={async (stepIndex, stepData, isDirty) => {
          // if (isDirty) {
          //   await fetch(`/api/complaints/${complaintId}/step/${stepIndex}`, {
          //     method: 'PATCH',
          //     headers: { 'Content-Type': 'application/json' },
          //     body: JSON.stringify(stepData)
          //   });
          // }
        }}
        enableApiSave={true}
        saveOnStepChange={true}
        trackDirtyState={true}
        autoSave={true}
        persistProgress={true}
        allowSkip={true}
        showDraftResume={true}
      />
    </div>
  );
};